import { graphql, readInlineData } from 'react-relay/legacy';
import braintreeClient from 'braintree-web/client';
import braintree3ds from 'braintree-web/three-d-secure';
import { localStorage, sessionStorage } from 'dibs-browser-storage';
import { goToConfirmation, getParamFromUrl, goToOMT } from '../helpers/checkoutRedirects';
import { trackAction, trackServiceError, trackTaxExempt } from '../helpers/checkoutTracking';
import {
    getConfirmationKey,
    getTriggerTypeKey,
    setConfirmationGuestUserToken,
} from '../helpers/checkoutStorage';
import { STORAGE, ERRORS } from '../constants/checkoutConstants';
import { logCheckoutError } from '../helpers/logCheckoutError';
import { getDeniedPaymentAuthorizationError } from '../helpers/getDeniedPaymentAuthorizationError';
import { removeLocalCartItem } from 'dibs-buyer-layout/exports/cartActions';
import {
    startCheckoutProcessingAction,
    setExitCheckoutAction,
    setTriggeredErrors,
} from './checkoutUIActions';
import { setProcessingErrorAction } from './commonActions';

// mutations
import CheckoutMutationWrapper from '../mutations/CheckoutMutationWrapper';
import paymentDataFor3dsMutation from '../mutations/paymentDataFor3ds';

function submitSucceed(payload) {
    const { transaction } = payload;
    const transactionId = transaction?.serviceId;
    const paymentType =
        transaction?.summary?.checkoutPaymentAuthorization?.paymentMethod?.paymentMethodType;
    const triggerType = getParamFromUrl('triggerType');

    if (triggerType) {
        sessionStorage.setItem(getTriggerTypeKey(transactionId), triggerType);
    }

    localStorage.setItem(getConfirmationKey(transactionId), true);
    localStorage.deleteItem(STORAGE.SURVEY_INFO);
    sessionStorage.deleteItem(STORAGE.AUTO_MODAL_INFO);
    sessionStorage.deleteItem(STORAGE.AUTO_MODAL_OFFER_INFO);
    setConfirmationGuestUserToken(transactionId);

    trackAction({
        action: 'complete',
        isInteractiveEvent: false,
        paymentType,
        eventNameValue: 'begin_checkout',
        stepInteractionValue: 'complete',
        stepValue: undefined,
    });
    removeLocalCartItem(transaction?.item?.serviceId);
    if (transaction?.isStripe3dsRejected) {
        trackAction({
            action: 'stripe 3ds rejected',
            isInteractiveEvent: false,
            paymentType,
            eventNameValue: 'stripe 3ds rejected',
            stepValue: undefined,
        });
        goToOMT(transactionId);
    } else {
        goToConfirmation(transactionId);
    }
}

function submitFailed(payload) {
    const { errorStatus, errorMessage, dispatch } = payload;

    if (errorStatus) {
        trackServiceError({
            action: 'checkout|review|ecom',
            label: errorStatus,
        });
        dispatch(setTriggeredErrors([errorStatus]));
    }

    dispatch(
        setProcessingErrorAction({
            errorStatus,
            errorMessage,
        })
    );
    dispatch({ type: 'TRANSACTION_SUBMIT_ERROR', errorStatus, errorMessage });
    dispatch(
        setProcessingErrorAction({
            errorStatus,
            errorMessage,
        })
    );
}

export const setAsTaxExempt = isTaxExempt => {
    trackTaxExempt({
        label: isTaxExempt ? 'tax exempt checkbox selected' : 'tax exempt checkbox not selected',
    });
    return { type: 'TRANSACTION_TAX_EXEMPT', isTaxExempt };
};

export const setVatReverseCharge = isVatReverseCharge => {
    return { type: 'TRANSACTION_VAT_REVERSE_CHARGE', isVatReverseCharge };
};

async function submitBraintreeCreditCard(
    checkoutMutation,
    { relay, transactionRef, viewerRef, transactionState, dispatch }
) {
    const { paymentMethodToken, buyerChargeCurrency } = transactionState;

    //3ds modal testing
    const params = new URLSearchParams(window.location.search);
    const always3dsViaQueryParam = params.get('always3DS');

    //get graphql data
    const transactionData = readInlineData(
        graphql`
            fragment checkoutSubmitActions_transaction on Transaction @inline {
                buyerInvoice {
                    totalAmount {
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                }
            }
        `,
        transactionRef
    );
    const viewerData = readInlineData(
        graphql`
            fragment checkoutSubmitActions_viewer on Viewer @inline {
                disable3dsVerificationForCheckout: isEnabled(
                    key: "disable3dsVerificationForCheckout"
                )
                default3dsAuthorizationAmountToOne: isEnabled(key: "zero3dsAmounts")
            }
        `,
        viewerRef
    );

    //3ds check
    try {
        if (!viewerData?.disable3dsVerificationForCheckout) {
            const paymentDataMutationResponse = await paymentDataFor3dsMutation(relay.environment, {
                paymentMethodToken,
                accountType: 'TRANSACTION',
                currency: buyerChargeCurrency,
            });

            const do3ds = ['psd2', 'PSDTWO'].includes(
                paymentDataMutationResponse?.paymentDataFor3ds?.paymentMethodNonce
                    ?.regulationEnvironment
            );

            if (do3ds || always3dsViaQueryParam) {
                //3ds is required
                trackAction({ action: '3ds implicated', isInteractiveEvent: false });

                const authorizationAmountObject =
                    (transactionData?.buyerInvoice?.totalAmount?.convertedAmountList || []).find(
                        amount => amount.currency === buyerChargeCurrency
                    ) || {};

                let amount = authorizationAmountObject?.amount;

                if (viewerData?.default3dsAuthorizationAmountToOne && amount === 0) {
                    amount = 1;
                }

                const clientInstance = await braintreeClient.create({
                    authorization:
                        paymentDataMutationResponse?.paymentDataFor3ds?.clientToken?.value,
                });
                const threeDSecureInstance = await braintree3ds.create({
                    client: clientInstance,
                    version: 2,
                });

                const responseFrom3ds = await threeDSecureInstance.verifyCard({
                    amount,
                    bin: paymentDataMutationResponse?.paymentDataFor3ds?.paymentMethodNonce?.bin,
                    nonce: paymentDataMutationResponse?.paymentDataFor3ds?.paymentMethodNonce
                        ?.nonce,
                    onLookupComplete: (lookupData, next) => {
                        next();
                    },
                });

                if (!responseFrom3ds.liabilityShifted) {
                    //user has not successfully verified card
                    trackAction({ action: '3ds verification failure', isInteractiveEvent: false });
                    submitFailed({
                        errorStatus: 'THREE_DS',
                        errorMessage: null,
                        dispatch,
                    });
                    return null;
                } else {
                    // fire checkout mutation with new 3DS nonce
                    return checkoutMutation({
                        paymentMethodNonce: responseFrom3ds?.nonce,
                        is3dsVerified: true,
                    });
                }
            } else {
                //3ds not required based on authentication insight
                return checkoutMutation();
            }
        } else {
            //3ds not required because it isn't a credit card transaction and/or disabler is on
            return checkoutMutation();
        }
    } catch (error) {
        if (!error.source) {
            //braintree error
            trackAction({ action: 'braintree error', isInteractiveEvent: false });
        }
        throw error;
    }
}

export async function submit(payload) {
    //TODO make TransactionState a helper
    const {
        relay,
        dispatch,
        transactionState,
        viewer: viewerRef,
        transaction: transactionRef,
    } = payload;

    dispatch(startCheckoutProcessingAction());

    const {
        transactionId,
        transactionType,
        itemId,
        skuId,
        buyerId,
        buyerChargeCurrency,
        isTaxExempt,
        shipmentType,
        shipmentQuoteId,
        shipmentNotes,
        shippingAddressId,
        paymentMethodApiType,
        paymentMethodToken,
        paymentProcessor,
        authorizationCode,
        quantity,
        promoCode,
        sidemark,
        projectId,
        offerNotes,
        offerAmountPerItem,
        offerCurrency,
        currencyConversionRateEventId,
        billingAddress,
        isGift,
        giftMessage,
        isVatReverseCharge,
    } = transactionState;

    async function checkoutMutation(newData = {}) {
        try {
            const response = await CheckoutMutationWrapper.commit(
                viewerRef?.isMultiStageCheckoutOn
                    ? 'TransactionMultiStageCheckout'
                    : 'TransactionCheckout',
                relay.environment,
                {
                    transactionId,
                    transactionType,
                    itemId,
                    skuId,
                    buyerId,
                    buyerChargeCurrency,
                    isTaxExempt,
                    shipmentType,
                    shipmentQuoteId,
                    shipmentNotes,
                    shippingAddressId,
                    paymentMethodApiType,
                    paymentMethodToken,
                    paymentProcessor,
                    authorizationCode,
                    quantity,
                    promoCode,
                    sidemark,
                    projectId,
                    offerNotes,
                    offerAmountPerItem,
                    offerCurrency,
                    currencyConversionRateEventId,
                    is3dsVerified: false,
                    billingAddress,
                    isGift,
                    giftMessage,
                    isVatReverseCharge,
                    ...newData,
                }
            );

            const savedTransaction = viewerRef?.isMultiStageCheckoutOn
                ? response?.transactionMultiStageCheckout?.transaction
                : response?.transactionCheckout?.transaction;
            dispatch(setExitCheckoutAction(true));
            submitSucceed({
                transaction: savedTransaction,
            });
        } catch (mutationError) {
            let errorInfo;
            let errorStatus;
            let errorMessage;

            const error = mutationError?.source?.errors?.[0];
            const messageFromError = error?.message || '';

            if (messageFromError.includes('THREE_DS_REQUIRED')) {
                trackAction({ action: 'braintree 3ds error', isInteractiveEvent: false });
                errorStatus = ERRORS.THREE_DS_REQUIRED;
                errorMessage =
                    'We were unable to authorize your payment. Please try another form of payment.';
            } else if (messageFromError.includes('has >0 bids')) {
                errorStatus = ERRORS.AUCTION_HAS_BID;
            } else if (messageFromError.includes('AuctionLot is already paused')) {
                errorStatus = ERRORS.AUCTION_IS_PAUSED;
            } else if (
                viewerRef?.isFineGrainedCheckoutErrorsEnabled &&
                !!getDeniedPaymentAuthorizationError(messageFromError)
            ) {
                errorStatus = getDeniedPaymentAuthorizationError(messageFromError);
            } else if (messageFromError.includes('CANNOT_CHECKOUT_MISSING_ADDRESS_DETAILS')) {
                errorStatus = ERRORS.SHIPPING_ERROR;
            } else {
                trackAction({ action: 'checkout submission error', isInteractiveEvent: false });
                errorInfo = error?.requestInfo;
                errorStatus = messageFromError.includes('PAYMENT_METHOD_ERROR')
                    ? ERRORS.PAYMENT_METHOD_ERROR
                    : errorInfo?.status || 'DEFAULT';
                errorMessage = errorInfo?.responsePayload?.message;
            }

            logCheckoutError(mutationError, 'submit', { errorStatus, errorMessage });
            submitFailed({ errorStatus, errorMessage, dispatch });
        }
    }

    try {
        if (paymentMethodApiType === 'creditCard' && paymentProcessor === 'BRAINTREE') {
            return submitBraintreeCreditCard(checkoutMutation, {
                relay,
                transactionRef,
                viewerRef,
                transactionState,
                dispatch,
            });
        } else {
            return checkoutMutation();
        }
    } catch (error) {
        if (error.source) {
            trackAction({ action: 'checkout submission error' });
        }

        const errorInfo = error?.source?.errors?.[0]?.requestInfo;
        const errorStatus = errorInfo?.status || 'DEFAULT';
        const errorMessage = errorInfo?.responsePayload?.message;
        logCheckoutError(error, 'submit', { errorStatus, errorMessage });

        return submitFailed({
            errorStatus,
            errorMessage,
            dispatch,
        });
    }
}
